import ClickableTableCell from 'Components/ClickableTableCell'
import CustomTablePagination from 'Components/CustomTablePagination'
import type { HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import EnhancedTableHead from 'Components/EnhancedTableHead/EnhancedTableHead'
import LightTooltip from 'Components/LightTooltip'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import { getAuthorityIdFromProductId } from 'Services/SalesManagement/Features/TicketSale/ticketSaleProductInfo'
import type { BulkTicketSale } from 'Services/SalesManagement/Features/TicketSale/types'
import { datetimeFormatterWithoutSeconds } from 'Util/formatters'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import { Paper, Table, TableBody, TableCell, TableContainer } from '@mui/material'
import { t } from 'ttag'

interface ProcessedCountCellProps {
  processedCount: number
  hasError: boolean
  isGenerating: boolean
}

function ProcessedCountCell({
  processedCount,
  hasError,
  isGenerating
}: Readonly<ProcessedCountCellProps>) {
  const errorMessage = t`There was an error generating the tickets`
  const errorTooltip = (
    <LightTooltip title={errorMessage} placement="top">
      <ErrorOutlineOutlinedIcon
        titleAccess={errorMessage}
        sx={{ ml: 1, display: 'inline-flex', verticalAlign: 'middle' }}
        color="error"
      />
    </LightTooltip>
  )
  const generatingMessage = t`Ticket generation in progress`
  const generatingTooltip = (
    <LightTooltip title={generatingMessage} placement="top">
      <AccessTimeOutlinedIcon
        titleAccess={generatingMessage}
        sx={{ ml: 1, display: 'inline-flex', verticalAlign: 'middle' }}
      />
    </LightTooltip>
  )
  return (
    <TableCell>
      {processedCount}
      {hasError && errorTooltip}
      {isGenerating && generatingTooltip}
    </TableCell>
  )
}

interface BulkTicketSaleListProps {
  rows: BulkTicketSale[]
  isLoading: boolean
  onRowClick: (id: string) => void
  selectedBulkTicketSaleId?: string
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
}

export default function BulkTicketSaleList({
  rows,
  isLoading,
  onRowClick,
  selectedBulkTicketSaleId,
  handleChangePage,
  count,
  page
}: Readonly<BulkTicketSaleListProps>) {
  const { getAuthorityNameByCode } = useAuthorityContext()
  const headCells: HeadCell[] = [
    { id: 'event_identification', label: t`Event identifier` },
    { id: 'total_requested_quantity', label: t`Ticket quantity` },
    { id: 'total_processed_count', label: t`Active tickets` },
    { id: 'zone', label: t`Zone` },
    { id: 'authority', label: t`Authority` },
    { id: 'valid_from', label: t`Validity starts` },
    { id: 'valid_to', label: t`Validity ends` },
    { id: 'created', label: t`Created` }
  ]
  return (
    <WithLoader loading={isLoading}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table size="small" aria-label={t`Bulk ticket sales table`}>
            {/* TODO: sorting, filtering */}
            <EnhancedTableHead headCells={headCells} onRequestSort={() => undefined} orderBy="" />
            <TableBody>
              {rows.map(row => (
                <StyledTableRow
                  hover
                  tabIndex={-1}
                  key={row.id}
                  selected={selectedBulkTicketSaleId === row.id}
                >
                  <ClickableTableCell sx={{ paddingY: 2 }} onClick={() => onRowClick(row.id)}>
                    {row.event_identification}
                  </ClickableTableCell>
                  <TableCell>{row.total_requested_quantity - row.total_cancelled_count}</TableCell>
                  <ProcessedCountCell
                    processedCount={row.total_processed_count}
                    hasError={row.has_failed_batches}
                    isGenerating={row.has_processing_batches}
                  />
                  <TableCell>{`${row.external_zone_from_name} - ${row.external_zone_to_name}`}</TableCell>
                  <TableCell>
                    {getAuthorityNameByCode(getAuthorityIdFromProductId(row.external_product_id))}
                  </TableCell>
                  <TableCell>{datetimeFormatterWithoutSeconds(row.valid_from)}</TableCell>
                  <TableCell>{datetimeFormatterWithoutSeconds(row.valid_to)}</TableCell>
                  <TableCell>{datetimeFormatterWithoutSeconds(row.created)}</TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}
